import { MenuItem, Tooltip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import axios from "const/axios";
import DisplayDate from "components/DisplayDate";
import { differenceInDays } from "date-fns";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { hasPermission } from "utils/permissionUtils";

const ViewClientTasks = ({ clientData }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    data: clientTaskData = [],
    isFetching: isLoading,
    refetch: refetchClientTaskData,
  } = useQuery({
    queryKey: ["clientTaskData", id],
    queryFn: async () => {
      const res = await axios.get(`/tasks/clients/${id}`);
      return res?.data?.data ?? [];
    },
    enabled: !!id,
    refetchOnWindowFocus: false,
  });

  // useEffect(() => {
  //   if (id) {
  //     refetchClientTaskData();
  //   }
  // }, [id]);

  const getPriorityColor = (priority) => {
    // Ensure priority is a string before calling toLowerCase
    const priorityString = typeof priority === "string" ? priority.toLowerCase() : "";
    switch (priorityString) {
      case "high":
        return "bg-red-100 text-red-800";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      case "low":
        return "bg-green-100 text-green-800";
      default:
        return "bg-app-surface-muted text-app-text";
    }
  };

  const getStatusColor = (status) => {
    // Ensure status is a string before calling toLowerCase
    const statusString = typeof status === "string" ? status.toLowerCase() : "";
    switch (statusString) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "in progress":
        return "bg-blue-100 text-blue-800";
      case "completed":
        return "bg-green-100 text-green-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-app-surface-muted text-app-text";
    }
  };
  const isDeadlinePassed = useCallback((taskDetails) => {
    const daysUntilDeadline = differenceInDays(new Date(taskDetails?.deadline), new Date());
    if (taskDetails?.status === "COMPLETED" || taskDetails?.status === "CANCELLED") {
      return false;
    } else {
      return { days: daysUntilDeadline + 1, isPassed: daysUntilDeadline < 0 };
    }
  }, []);

  const columns = useMemo(
    () => [
      {
        accessorKey: "specificTask",
        header: "Title",
        size: 100,
      },
      {
        accessorKey: "module",
        header: "Module",
        size: 100,
      },
      {
        id: "createdDate",
        accessorFn: (row) => row.createdDate ?? row.createdAt,
        header: "Created Date",
        size: 100,
        Cell: ({ renderedCellValue }) => <DisplayDate value={renderedCellValue} />,
      },
      {
        accessorKey: "deadline",
        header: "Deadline",
        size: 150,
        Cell: ({ row }) => {
          return (
            <div
              className={`flex flex-col items-start max-w-[150px] truncate ${
                isDeadlinePassed(row?.original)?.isPassed ? "text-red-500" : ""
              }`}
            >
              <DisplayDate value={row?.original?.deadline} />
              {isDeadlinePassed(row?.original)?.days > 0 && (
                <span className="text-xs text-app-text-secondary">
                  {isDeadlinePassed(row?.original)?.days}
                  day{isDeadlinePassed(row?.original)?.days > 1 ? "s" : ""} left
                </span>
              )}
              {isDeadlinePassed(row?.original)?.days <= 0 && (
                <span className="text-xs text-red-500">Deadline already passed.</span>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "assignedBy.name",
        header: "Assigned By",
        size: 150,
        Cell: ({ renderedCellValue }) => {
          return (
            <Tooltip title={renderedCellValue}>
              <div className="flex items-center">{renderedCellValue}</div>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: "assignedTo.name",
        header: "Assigned To",
        size: 150,
        Cell: ({ renderedCellValue }) => {
          return (
            <Tooltip title={renderedCellValue}>
              <div className="flex items-center">{renderedCellValue}</div>
            </Tooltip>
          );
        },
      },

      {
        accessorKey: "priority",
        header: "Priority",
        size: 150,
        Cell: ({ renderedCellValue }) => {
          return (
            <Tooltip title={renderedCellValue}>
              <div className={`flex items-center`}>
                <span
                  className={`px-2 py-1 rounded-full text-sm font-medium capitalize ${getPriorityColor(
                    renderedCellValue
                  )}`}
                >
                  {renderedCellValue}
                </span>
              </div>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 150,
        Cell: ({ renderedCellValue }) => {
          return (
            <Tooltip title={renderedCellValue}>
              <div className={`flex items-center`}>
                <span
                  className={`px-2 py-1 rounded-full text-sm font-medium capitalize 
                    ${getStatusColor(renderedCellValue)}
                  `}
                >
                  {renderedCellValue}
                </span>
              </div>
            </Tooltip>
          );
        },
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    enableRowNumbers: true,
    data: clientTaskData?.length > 0 ? clientTaskData : [], // Use API data or fallback to mock data
    enableRowActions: true,
    muiTableContainerProps: {
      sx: {
        maxHeight: "70vh",
        minHeight: "400px",
        overflowY: "auto",
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (event) => {
        // Check if the click was on an action button or its child
        const isActionClick =
          event.target.closest(".MuiIconButton-root") || event.target.closest(".MuiPopover-root");
        if (!isActionClick) {
          navigate(`/admin/tasks/view/${row.original.id}`, {
            state: {
              item: row?.original,
            },
          });
        }
      },
      sx: {
        cursor: "pointer",
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.04)",
        },
      },
    }),

    renderRowActionMenuItems: ({ row }) => [
      <MenuItem
        key="view"
        onClick={() =>
          navigate(`/admin/tasks/view/${row.original.id}`, {
            state: {
              item: row?.original,
            },
          })
        }
      >
        View
      </MenuItem>,
    ],
    positionActionsColumn: "last",
    initialState: {
      enableGlobalFilter: false,
      showGlobalFilter: false,
      density: "normal",
      pagination: {
        pageSize: 10,
        pageIndex: 0,
      },
    },

    enablePagination: true,
    enableStickyHeader: true,
    enableColumnPinning: true,
    manualPagination: false,
    muiTableContainerProps: {
      sx: {
        maxHeight: "70vh",
        minHeight: "400px",
        overflowY: "auto",
      },
    },

    state: {
      isLoading: isLoading,
      showProgressBars: isLoading,
    },
  });

  return (
    <div className="bg-app-surface rounded-lg shadow-sm">
      <MaterialReactTable table={table} />
    </div>
  );
};

export default ViewClientTasks;
